import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { useKindeBrowserClient } from '@kinde-oss/kinde-auth-nextjs';
import { useState, useEffect } from 'react';

import { AUTH_CONFIG } from '@/src/network/auth';
import { isAnonymousParticipantToken } from '@/src/network/authExchange';
import { isEmbeddedByAnotherOrigin, isInIframe } from '@/utils/isInIframe';
import { TypedStorage } from '@/utils/TypedStorage';

import type { ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

/**
 * The participant app's iframe runs on the token it hands over, never Kinde's: Kinde there can be another
 * user, or reset mid-call and leave `getToken` empty. Same-origin frames are sessions' own previews, where
 * only an anonymous token wins. The URL check covers the time before `AuthTokenProvider` stores the token.
 */
const hasEmbeddedParticipantSession = (): boolean => {
  if (!isInIframe()) {
    return false;
  }
  const token = new URLSearchParams(window.location.search).get('userToken') ?? TypedStorage.get('viewer_jwt');
  return !!token && (isAnonymousParticipantToken(token) || isEmbeddedByAnotherOrigin());
};

export const SetUpKinde = ({ children }: Props) => {
  const { getToken, isLoading, isAuthenticated, refreshData } = useKindeBrowserClient();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isAuthenticated && !hasEmbeddedParticipantSession()) {
      AUTH_CONFIG.getToken = getToken;
      // Force Kinde to refresh the token then return the fresh value, so the
      // URQL `authExchange.refreshAuth` actually has a way to recover from an
      // expired token.
      AUTH_CONFIG.refreshToken = async () => {
        await refreshData();
        return getToken() ?? undefined;
      };
    }

    setReady(true);
  }, [isLoading]);

  if (!ready) {
    return <AskableIconLoading isFullScreen />;
  }

  return children;
};
